<script setup>
import { computed, h, ref, onMounted } from 'vue';
import { useI18n } from 'vue-i18n';
import { useRoute } from 'vue-router';
import { picoSearch } from '@scmmishra/pico-search';
import { useStore, useMapGetter } from 'dashboard/composables/store';
import { useAlert } from 'dashboard/composables';
import { useUISettings } from 'dashboard/composables/useUISettings';
import { useMessageFormatter } from 'shared/composables/useMessageFormatter';
import Button from 'dashboard/components-next/button/Button.vue';
import Input from 'dashboard/components-next/input/Input.vue';

import PageLayout from 'dashboard/components-next/captain/PageLayout.vue';
import SettingsHeader from 'dashboard/components-next/captain/pageComponents/settings/SettingsHeader.vue';
import SuggestedScenarios from 'dashboard/components-next/captain/assistant/SuggestedRules.vue';
import ScenariosCard from 'dashboard/components-next/captain/assistant/ScenariosCard.vue';
import BulkSelectBar from 'dashboard/components-next/captain/assistant/BulkSelectBar.vue';
import AddNewScenariosDialog from 'dashboard/components-next/captain/assistant/AddNewScenariosDialog.vue';

const { t } = useI18n();
const route = useRoute();
const store = useStore();
const { uiSettings, updateUISettings } = useUISettings();
const { formatMessage } = useMessageFormatter();
const assistantId = computed(() => Number(route.params.assistantId));

const uiFlags = useMapGetter('captainScenarios/getUIFlags');
const isFetching = computed(() => uiFlags.value.fetchingList);
const scenarios = useMapGetter('captainScenarios/getRecords');

const searchQuery = ref('');

const LINK_INSTRUCTION_CLASS =
  '[&_a[href^="tool://"]]:text-n-iris-11 [&_a:not([href^="tool://"])]:text-n-slate-12 [&_a]:pointer-events-none [&_a]:cursor-default';

const renderInstruction = instruction => () =>
  h('span', {
    class: `text-sm text-n-slate-12 py-4 prose prose-sm min-w-0 break-words ${LINK_INSTRUCTION_CLASS}`,
    innerHTML: instruction,
  });

// Suggested example scenarios for quick add
const scenariosExample = [
  {
    id: 1,
    title: 'Ecommerce Sales Advisor',
    description:
      'Consultatively help customers discover promotions, compare products, and buy through VTEX without turning the chat into a questionnaire',
    instruction: [
      'If the customer wants to buy, compare, find, or confirm a product, act like a subtle but effective ecommerce sales advisor. Be warm, clear, and commercially helpful without sounding pushy. Reduce friction, keep momentum, and make the next step feel easy.',
      'Do not block product discovery just because lead data is still missing. Help with product search first whenever you already have enough buying intent to continue. Ask for the customer name, city, email, and phone only when it is useful for the next business step, such as creating a VTEX customer, opening a draft order, generating checkout, or handing off. Do not repeat the same lead-data request in consecutive turns. When the customer shares those details, use [Update Contact](tool://update_contact) to persist the local contact profile, then save the buying context using [Add Private Note](tool://add_private_note). Keep the conversation stage updated with [Update Conversation Stage](tool://update_conversation_stage), but only when the sales state materially changes. If VTEX customer lookup is available and the contact now has enough identifiers, use [VTEX](tool://vtex) to check whether the customer already exists and create it if needed. Before suggesting an internal VTEX draft order, cart, or checkout flow, make sure those lead fields are already captured.',
      'Understand the buying intent quickly. If the customer already gave enough detail, do not delay with more questions. A best-effort search is usually better than another discovery question. Ask at most one short qualification question before the first recommendation, and only when the request is so broad that you truly cannot form a useful first search. If the customer already gave a category, a budget, a brand, a price tier, or directly asked to see options, search first and recommend. After that first question, use the very next useful clue from the customer to search immediately, even if it is only a short fragment, a misspelling, a budget amount, a partial model, or a loose reference. Treat follow-up replies such as "dólares", "usd", "otro", "otra", "cualquiera", "uno de 100", "envíame los modelos", or "muéstrame lo que tengas" as continuation cues that must reuse the current context, not as a reason to restart discovery. Once the customer has already answered one qualification question, do not ask another different one before searching.',
      'Preserve the requested product category across turns. If the customer first asks for a phone and then adds a brand, budget, or preference, search and recommend within phones for that context, not the whole catalog. If VTEX only returns products from that brand in other categories, do not present them as recommendations. Say clearly that you could not find that brand in the requested category, and then offer same-category alternatives if VTEX provides them. Only explore another category if the customer explicitly asks for it.',
      'Use [VTEX](tool://vtex) to search products, confirm variants, and check product context before recommending anything. Present at most 3 options in one reply, and make the answer easy to buy from: product name, price, variant or color when relevant, and one short reason why it matches. If one option is clearly the best fit, say so directly. Outside Messenger, when you recommend 2 or 3 concrete options and the customer needs to choose one, you may use [Send Product Options](tool://send_product_options) so the customer can tap the exact product. Do not use Send Product Options for product recommendations in Messenger when product URLs are available, because those quick replies disappear after the next message. Keep those labels short and specific when you do use them. If the customer asks to see what you have, asks about promotions, asks for a price tier like "gama media", or says they want something affordable, send the options immediately instead of asking for more details. If the customer says "otro" after you already recommended options, keep the same category and known constraints, then offer the next best alternative. If the customer says "cualquiera", pick the best-fit option under the known constraints and explain why in one short sentence. Ignore out-of-stock products and out-of-stock colors whenever in-stock options exist.',
      'When the customer asks for a specific SKU, ref, exact variant, or refers to a product by price like "el de 483", answer about that exact product first. If nothing exact is found, offer the closest alternatives. If direct matches are empty but VTEX returns `alternative_products`, offer those before saying the item is unavailable. Do not jump to accessories or complementary items before the main requested product category is resolved. If VTEX does not find products in the requested main category, stay in that category: offer same-category alternatives when available, otherwise ask one brief same-category follow-up or say clearly that there is no stock. Never stop at "no hay" if VTEX already returned useful same-category alternatives.',
      'Do not tell the customer you had a technical problem after a single failed search. First retry mentally by broadening within the same category and using the context you already have. Only mention a limitation if you truly cannot continue with a useful recommendation or fallback.',
      'For Messenger and Instagram, keep the recommendation experience in one consistent message while exploring options, and do not send separate image messages while you are still comparing multiple products. In Messenger, if you are showing 2 or 3 concrete products and each one has `product_url`, prefer one [Send Product Link](tool://send_product_link) call with `items_json` so the customer receives a horizontal native carousel. If [VTEX](tool://vtex) already returned `messenger_items_json`, or returned `recommended_delivery` for Messenger, use that exact delivery payload first instead of rewriting product links by hand, and do not replace it with a plain text list of products or pasted URLs. Include `sku_id` or `ref_id` in each item whenever you have them so the default buttons can work inside the chat. In Messenger, never paste store product URLs in the text body when a product recommendation already has `product_url` or `messenger_items_json`, never send numbered product bullets with pasted URLs, and do not duplicate an available carousel with another product list in text. In Messenger product cards and carousels, the default buttons should be Más información, Añadir al carrito, and Ver producto ↗. If the customer taps Añadir al carrito, use [VTEX](tool://vtex) to add that exact product to the conversation cart and keep the sale moving inside chat. Do not send a single product card automatically right after the customer selects an option. Reserve the individual Messenger card for when the customer explicitly asks for more information, asks to see that exact product, or taps Más información. Use [Send Image Message](tool://send_image_message) only when the customer explicitly asked to see the image or when you are sending a standalone image without a product card. If VTEX returned `product_url`, use [Send Product Link](tool://send_product_link) so the customer can open the store page.',
      'Once the customer already selected a product, you may use [VTEX](tool://vtex) with `cross_sell` to suggest 1 or 2 genuinely useful complementary items, such as headphones for a phone, a mouse for a laptop, or paper for a printer, but keep it brief and optional. Do not send checkout immediately after the product selection. First ask one brief follow-up question such as whether they want anything else or whether you can continue with checkout. Only after the customer answers that follow-up and confirms they want to continue should you use [VTEX](tool://vtex) to create the checkout link, update the labels to checkout and payment-pending, and tell the customer to open the button or link you just sent instead of pasting the raw checkout URL yourself. If later the customer says they already paid or finished checkout, use [VTEX](tool://vtex) again to verify the checkout before confirming the purchase, and if it is approved update the labels to won and paid.',
      'If a reusable approved channel template makes the next step easier, use [Message Templates](tool://message_templates) to list or send the right template for the current inbox. Prefer utility-style WhatsApp templates when the customer requested the information. For Messenger and Instagram, use templates when they make the path clearer, especially for URL buttons instead of raw links.',
      'Replies such as "ok", "sí", "dale", "perfecto", or "si claro" after product suggestions, option exploration, or image offers mean continue helping the customer, not handoff. Only hand off when you already offered a human advisor and the customer is clearly accepting that offer. If you already offered to connect the customer with a human advisor and the customer replies with a short affirmative such as "ok", "sí", "dale", or "perfecto", use [Update Conversation Stage](tool://update_conversation_stage) to set the handoff stage and then use [Handoff to Human](tool://handoff) immediately. If the customer needs human negotiation, custom pricing, unavailable inventory handling, or something you cannot complete safely, explain that a teammate will continue, update the conversation stage, and use [Handoff to Human](tool://handoff).',
    ].join('\n\n'),
    tools: [
      'update_contact',
      'update_conversation_stage',
      'vtex',
      'send_image_message',
      'send_product_options',
      'send_product_link',
      'message_templates',
      'add_private_note',
      'handoff',
    ],
  },
];

const filteredScenarios = computed(() => {
  const query = searchQuery.value.trim();
  const source = scenarios.value;
  if (!query) return source;
  return picoSearch(source, query, ['title', 'description', 'instruction']);
});

const shouldShowSuggestedRules = computed(() => {
  return uiSettings.value?.show_scenarios_suggestions !== false;
});

const closeSuggestedRules = () => {
  updateUISettings({ show_scenarios_suggestions: false });
};

// Bulk selection & hover state
const bulkSelectedIds = ref(new Set());
const hoveredCard = ref(null);

const handleRuleSelect = id => {
  const selected = new Set(bulkSelectedIds.value);
  selected[selected.has(id) ? 'delete' : 'add'](id);
  bulkSelectedIds.value = selected;
};

const buildSelectedCountLabel = computed(() => {
  const count = scenarios.value.length || 0;
  const isAllSelected = bulkSelectedIds.value.size === count && count > 0;
  return isAllSelected
    ? t('CAPTAIN.ASSISTANTS.SCENARIOS.BULK_ACTION.UNSELECT_ALL', { count })
    : t('CAPTAIN.ASSISTANTS.SCENARIOS.BULK_ACTION.SELECT_ALL', { count });
});

const selectedCountLabel = computed(() => {
  return t('CAPTAIN.ASSISTANTS.SCENARIOS.BULK_ACTION.SELECTED', {
    count: bulkSelectedIds.value.size,
  });
});

const handleRuleHover = (isHovered, id) => {
  hoveredCard.value = isHovered ? id : null;
};

const getToolsFromInstruction = instruction => [
  ...new Set(
    [...(instruction?.matchAll(/\(tool:\/\/([^)]+)\)/g) ?? [])].map(m => m[1])
  ),
];

const updateScenario = async scenario => {
  try {
    await store.dispatch('captainScenarios/update', {
      id: scenario.id,
      assistantId: assistantId.value,
      ...scenario,
      tools: getToolsFromInstruction(scenario.instruction),
    });
    useAlert(t('CAPTAIN.ASSISTANTS.SCENARIOS.API.UPDATE.SUCCESS'));
  } catch (error) {
    const errorMessage =
      error?.response?.message ||
      t('CAPTAIN.ASSISTANTS.SCENARIOS.API.UPDATE.ERROR');
    useAlert(errorMessage);
  }
};

const deleteScenario = async id => {
  try {
    await store.dispatch('captainScenarios/delete', {
      id,
      assistantId: assistantId.value,
    });
    useAlert(t('CAPTAIN.ASSISTANTS.SCENARIOS.API.DELETE.SUCCESS'));
  } catch (error) {
    const errorMessage =
      error?.response?.message ||
      t('CAPTAIN.ASSISTANTS.SCENARIOS.API.DELETE.ERROR');
    useAlert(errorMessage);
  }
};

// TODO: Add bulk delete endpoint
const bulkDeleteScenarios = async ids => {
  const idsArray = ids || Array.from(bulkSelectedIds.value);
  await Promise.all(
    idsArray.map(id =>
      store.dispatch('captainScenarios/delete', {
        id,
        assistantId: assistantId.value,
      })
    )
  );
  bulkSelectedIds.value = new Set();
  useAlert(t('CAPTAIN.ASSISTANTS.SCENARIOS.API.DELETE.SUCCESS'));
};

const addScenario = async scenario => {
  try {
    await store.dispatch('captainScenarios/create', {
      assistantId: assistantId.value,
      ...scenario,
      tools: getToolsFromInstruction(scenario.instruction),
    });
    useAlert(t('CAPTAIN.ASSISTANTS.SCENARIOS.API.ADD.SUCCESS'));
  } catch (error) {
    const errorMessage =
      error?.response?.message ||
      t('CAPTAIN.ASSISTANTS.SCENARIOS.API.ADD.ERROR');
    useAlert(errorMessage);
  }
};

const addAllExampleScenarios = async () => {
  try {
    scenariosExample.forEach(async scenario => {
      await store.dispatch('captainScenarios/create', {
        assistantId: assistantId.value,
        ...scenario,
      });
    });
    useAlert(t('CAPTAIN.ASSISTANTS.SCENARIOS.API.ADD.SUCCESS'));
  } catch (error) {
    const errorMessage =
      error?.response?.message ||
      t('CAPTAIN.ASSISTANTS.SCENARIOS.API.ADD.ERROR');
    useAlert(errorMessage);
  }
};

onMounted(() => {
  store.dispatch('captainScenarios/get', {
    assistantId: assistantId.value,
  });
  store.dispatch('captainTools/getTools');
});
</script>

<template>
  <PageLayout
    :header-title="$t('CAPTAIN.ASSISTANTS.SCENARIOS.TITLE')"
    :is-fetching="isFetching"
    :show-know-more="false"
    :show-pagination-footer="false"
  >
    <template #body>
      <SettingsHeader
        :heading="$t('CAPTAIN.ASSISTANTS.SCENARIOS.TITLE')"
        :description="$t('CAPTAIN.ASSISTANTS.SCENARIOS.DESCRIPTION')"
      />
      <div v-if="shouldShowSuggestedRules" class="flex mt-7 flex-col gap-4">
        <SuggestedScenarios
          :title="$t('CAPTAIN.ASSISTANTS.SCENARIOS.ADD.SUGGESTED.TITLE')"
          :items="scenariosExample"
          @close="closeSuggestedRules"
          @add="addAllExampleScenarios"
        >
          <template #default="{ item }">
            <div class="flex items-center gap-3 justify-between">
              <span class="text-sm text-n-slate-12">
                {{ item.title }}
              </span>
              <Button
                :label="
                  $t('CAPTAIN.ASSISTANTS.SCENARIOS.ADD.SUGGESTED.ADD_SINGLE')
                "
                ghost
                xs
                slate
                class="!text-sm !text-n-slate-11 flex-shrink-0"
                @click="addScenario(item)"
              />
            </div>
            <div class="flex flex-col">
              <span class="text-sm text-n-slate-11 mt-2">
                {{ item.description }}
              </span>
              <component
                :is="renderInstruction(formatMessage(item.instruction, false))"
              />
              <span class="text-sm text-n-slate-11 font-medium mb-1">
                {{ t('CAPTAIN.ASSISTANTS.SCENARIOS.ADD.SUGGESTED.TOOLS_USED') }}
                {{ item.tools?.map(tool => `@${tool}`).join(', ') }}
              </span>
            </div>
          </template>
        </SuggestedScenarios>
      </div>
      <div class="flex mt-7 flex-col gap-4">
        <div class="flex justify-between items-center">
          <BulkSelectBar
            v-model="bulkSelectedIds"
            :all-items="scenarios"
            :select-all-label="buildSelectedCountLabel"
            :selected-count-label="selectedCountLabel"
            :delete-label="
              $t('CAPTAIN.ASSISTANTS.SCENARIOS.BULK_ACTION.BULK_DELETE_BUTTON')
            "
            @bulk-delete="bulkDeleteScenarios"
          >
            <template #default-actions>
              <AddNewScenariosDialog @add="addScenario" />
            </template>
          </BulkSelectBar>
          <div
            v-if="scenarios.length && bulkSelectedIds.size === 0"
            class="max-w-[22.5rem] w-full min-w-0"
          >
            <Input
              v-model="searchQuery"
              :placeholder="
                t('CAPTAIN.ASSISTANTS.SCENARIOS.LIST.SEARCH_PLACEHOLDER')
              "
            />
          </div>
        </div>
        <div v-if="scenarios.length === 0" class="mt-1 mb-2">
          <span class="text-n-slate-11 text-sm">
            {{ t('CAPTAIN.ASSISTANTS.SCENARIOS.EMPTY_MESSAGE') }}
          </span>
        </div>
        <div v-else-if="filteredScenarios.length === 0" class="mt-1 mb-2">
          <span class="text-n-slate-11 text-sm">
            {{ t('CAPTAIN.ASSISTANTS.SCENARIOS.SEARCH_EMPTY_MESSAGE') }}
          </span>
        </div>
        <div v-else class="flex flex-col gap-2">
          <ScenariosCard
            v-for="scenario in filteredScenarios"
            :id="scenario.id"
            :key="scenario.id"
            :title="scenario.title"
            :description="scenario.description"
            :instruction="scenario.instruction"
            :tools="scenario.tools"
            :is-selected="bulkSelectedIds.has(scenario.id)"
            :selectable="
              hoveredCard === scenario.id || bulkSelectedIds.size > 0
            "
            @select="handleRuleSelect"
            @delete="deleteScenario(scenario.id)"
            @update="updateScenario"
            @hover="isHovered => handleRuleHover(isHovered, scenario.id)"
          />
        </div>
      </div>
    </template>
  </PageLayout>
</template>
